import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/layout';
import { EmptyState, ErrorState } from '@/components/data-display';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, QrCode, MoreHorizontal, Eye, Pencil, Copy, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiV1 } from '@/api/client';
import QRCodeStyling from 'qr-code-styling';

const categories = [
  { value: 'all', label: 'All Categories' },
  { value: 'business', label: 'Business' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'events', label: 'Events' },
  { value: 'social', label: 'Social' },
  { value: 'custom', label: 'Custom' },
];

// QR Thumbnail component for template cards
function QRThumbnail({ design, size = 80 }) {
  const containerRef = useRef(null);
  const qrInstanceRef = useRef(null);
  
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.innerHTML = '';
      
      // Get logo URL from various possible locations in the design
      let logoUrl = design?.image_options?.src || 
                    design?.image_options?.image_url ||
                    design?.logoUrl || 
                    design?.image || 
                    null;
      
      // Convert relative URLs to absolute URLs
      if (logoUrl && logoUrl.startsWith('/')) {
        logoUrl = `${window.location.origin}${logoUrl}`;
      }
      
      const qrOptions = {
        width: size,
        height: size,
        data: 'https://example.com',
        dotsOptions: {
          type: design?.dots_options?.type || 'square',
          color: design?.dots_options?.color || '#000000',
        },
        cornersSquareOptions: {
          type: design?.corners_square_options?.type || 'square',
          color: design?.corners_square_options?.color || design?.dots_options?.color || '#000000',
        },
        cornersDotOptions: {
          type: design?.corners_dot_options?.type || 'square',
          color: design?.corners_dot_options?.color || design?.dots_options?.color || '#000000',
        },
        backgroundOptions: {
          color: design?.background_options?.color || '#ffffff',
        },
      };
      
      // Add logo if present - with proper image options
      if (logoUrl) {
        qrOptions.image = logoUrl;
        qrOptions.imageOptions = {
          crossOrigin: 'anonymous',
          margin: design?.image_options?.margin || 5,
          imageSize: design?.image_options?.image_size || design?.image_options?.imageSize || 0.4,
          hideBackgroundDots: design?.image_options?.hide_background_dots !== false,
        };
      }
      
      try {
        qrInstanceRef.current = new QRCodeStyling(qrOptions);
        qrInstanceRef.current.append(containerRef.current);
      } catch (err) {
        console.error('Error generating QR thumbnail:', err);
      }
    }
    
    return () => {
      qrInstanceRef.current = null;
    };
  }, [design, size]);
  
  return <div ref={containerRef} />;
}

export default function Templates() {
  // /templates/:id opens that template in the preview dialog as soon
  // as the templates list has loaded. /templates renders the gallery
  // with no dialog. Closing the dialog navigates back to /templates so
  // the URL stays in sync.
  const { id: routeTemplateId } = useParams();
  const navigate = useNavigate();
  const [category, setCategory] = useState('all');
  const [sort, setSort] = useState('popular');
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [deleteTemplate, setDeleteTemplate] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [duplicating, setDuplicating] = useState(null);
  
  // Ref for QR code preview
  const qrPreviewRef = useRef(null);
  const qrCodeInstanceRef = useRef(null);

  // Generate QR code preview when template changes
  useEffect(() => {
    if (previewTemplate) {
      // Small delay to ensure dialog is mounted and ref is available
      const timer = setTimeout(() => {
        if (qrPreviewRef.current) {
          // Clear previous QR code
          qrPreviewRef.current.innerHTML = '';
          
          const design = previewTemplate.design || {};
          
          // Get logo URL from various possible locations in the design
          let logoUrl = design.image_options?.src || 
                        design.image_options?.image_url ||
                        design.logoUrl || 
                        design.image || 
                        null;
          
          // Convert relative URLs to absolute URLs
          if (logoUrl && logoUrl.startsWith('/')) {
            logoUrl = `${window.location.origin}${logoUrl}`;
          }
          
          console.log('Generating QR preview with design:', design);
          console.log('Logo URL found:', logoUrl);
          
          const qrOptions = {
            width: 200,
            height: 200,
            data: 'https://example.com/preview',
            dotsOptions: {
              type: design.dots_options?.type || 'square',
              color: design.dots_options?.color || '#000000',
            },
            cornersSquareOptions: {
              type: design.corners_square_options?.type || 'square',
              color: design.corners_square_options?.color || design.dots_options?.color || '#000000',
            },
            cornersDotOptions: {
              type: design.corners_dot_options?.type || 'square',
              color: design.corners_dot_options?.color || design.dots_options?.color || '#000000',
            },
            backgroundOptions: {
              color: design.background_options?.color || '#ffffff',
            },
          };
          
          // Add logo if present
          if (logoUrl) {
            qrOptions.image = logoUrl;
            qrOptions.imageOptions = {
              crossOrigin: 'anonymous',
              margin: design.image_options?.margin || 5,
              imageSize: design.image_options?.image_size || design.image_options?.imageSize || 0.4,
              hideBackgroundDots: design.image_options?.hide_background_dots !== false,
            };
          }
          
          try {
            qrCodeInstanceRef.current = new QRCodeStyling(qrOptions);
            qrCodeInstanceRef.current.append(qrPreviewRef.current);
          } catch (err) {
            console.error('Error generating QR preview:', err);
          }
        }
      }, 100);
      
      return () => clearTimeout(timer);
    }
    
    return () => {
      if (qrCodeInstanceRef.current) {
        qrCodeInstanceRef.current = null;
      }
    };
  }, [previewTemplate]);

  // Fetch templates from API
  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (category !== 'all') {
        params.append('category', category);
      }
      params.append('include_system', 'true');
      
      const response = await apiV1.get(`/qrcodes/templates/?${params.toString()}`);
      if (response.data?.success) {
        let data = response.data.data || [];
        
        // Sort templates
        if (sort === 'popular') {
          data.sort((a, b) => (b.usage_count || 0) - (a.usage_count || 0));
        } else if (sort === 'name') {
          data.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        } else if (sort === 'recent') {
          data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        }
        
        setTemplates(data);
      } else {
        setError('Failed to load templates');
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError(err.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [category, sort]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Open the matching template in the preview dialog when the URL
  // includes /:id. Runs whenever the id changes or the templates list
  // is refetched.
  useEffect(() => {
    if (!routeTemplateId || templates.length === 0) return;
    const match = templates.find((t) => t.id === routeTemplateId);
    if (match) {
      setPreviewTemplate(match);
    }
  }, [routeTemplateId, templates]);

  // Handle delete template
  const handleDelete = async () => {
    if (!deleteTemplate) return;
    
    // Check if it's a system template
    if (deleteTemplate.is_system || deleteTemplate.id?.startsWith('tpl_default') || 
        deleteTemplate.id?.startsWith('tpl_rounded') || deleteTemplate.id?.startsWith('tpl_branded') ||
        deleteTemplate.id?.startsWith('tpl_dots') || deleteTemplate.id?.startsWith('tpl_gradient') ||
        deleteTemplate.id?.startsWith('tpl_framed')) {
      toast.error('Cannot delete system templates');
      setDeleteTemplate(null);
      return;
    }
    
    setDeleting(true);
    try {
      const response = await apiV1.delete(`/qrcodes/templates/${deleteTemplate.id}`);
      if (response.data?.success) {
        toast.success('Template deleted successfully');
        setTemplates(prev => prev.filter(t => t.id !== deleteTemplate.id));
      } else {
        toast.error(response.data?.detail || 'Failed to delete template');
      }
    } catch (err) {
      console.error('Error deleting template:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to delete template';
      toast.error(errorMsg);
    } finally {
      setDeleting(false);
      setDeleteTemplate(null);
    }
  };

  // Handle duplicate template
  const handleDuplicate = async (template) => {
    setDuplicating(template.id);
    try {
      const response = await apiV1.post(`/qrcodes/templates/${template.id}/duplicate`);
      if (response.data?.success) {
        toast.success('Template duplicated successfully');
        // Add the new template to the list
        setTemplates(prev => [response.data.data, ...prev]);
      } else {
        toast.error('Failed to duplicate template');
      }
    } catch (err) {
      console.error('Error duplicating template:', err);
      toast.error(err.response?.data?.detail || 'Failed to duplicate template');
    } finally {
      setDuplicating(null);
    }
  };

  // Handle use template - navigate to QR creator with template
  const handleUseTemplate = (template) => {
    window.location.href = `/qrs/new?template=${template.id}`;
  };

  if (error) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Templates"
          description="Reusable QR code style templates"
        />
        <ErrorState message={error} onRetry={fetchTemplates} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="templates-page">
      <PageHeader
        title="Templates"
        description="Reusable QR code style templates"
        actions={
          <Button asChild>
            <Link to="/editor?from=templates&mode=create">
              <Plus className="h-4 w-4 mr-2" />
              Create Template
            </Link>
          </Button>
        }
      />

      {/* Filters */}
      <div className="flex gap-4">
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-[180px]" data-testid="category-filter">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            {categories.map((cat) => (
              <SelectItem key={cat.value} value={cat.value}>
                {cat.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="w-[150px]" data-testid="sort-filter">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="popular">Most Popular</SelectItem>
            <SelectItem value="name">Name A-Z</SelectItem>
            <SelectItem value="recent">Recently Added</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <Skeleton className="aspect-square rounded-lg mb-4" />
                <Skeleton className="h-5 w-3/4 mb-2" />
                <Skeleton className="h-4 w-1/2" />
              </CardContent>
              <CardFooter className="p-4 pt-0">
                <Skeleton className="h-9 w-full" />
              </CardFooter>
            </Card>
          ))}
        </div>
      ) : templates.length === 0 ? (
        <EmptyState
          icon={QrCode}
          title="No templates found"
          description="Try adjusting your filters or create a new template"
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {templates.map((template) => (
            <Card key={template.id} className="group card-hover" data-testid="template-card">
              <CardContent className="p-4">
                <div className="aspect-square bg-muted rounded-lg flex items-center justify-center mb-4 relative overflow-hidden">
                  {/* Display actual QR preview based on template design */}
                  <QRThumbnail design={template.design} size={100} />
                  {/* System badge */}
                  {template.is_system && (
                    <Badge variant="secondary" className="absolute top-2 left-2 text-xs">
                      System
                    </Badge>
                  )}
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-background/80 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 rounded-lg">
                    <Button size="sm" onClick={() => setPreviewTemplate(template)} data-testid="preview-button">
                      <Eye className="h-4 w-4 mr-1" />
                      Preview
                    </Button>
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium truncate" title={template.name}>
                      {template.name}
                    </h3>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid="template-menu">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setPreviewTemplate(template)}>
                          <Eye className="h-4 w-4 mr-2" />
                          Preview
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to={`/editor?template=${template.id}`}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Edit
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleDuplicate(template)}
                          disabled={duplicating === template.id}
                        >
                          {duplicating === template.id ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Copy className="h-4 w-4 mr-2" />
                          )}
                          Duplicate
                        </DropdownMenuItem>
                        {!template.is_system && (
                          <DropdownMenuItem 
                            className="text-destructive"
                            onClick={() => setDeleteTemplate(template)}
                            data-testid="delete-template-button"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs capitalize">
                      {template.category?.replace('_', ' ')}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      Used {template.usage_count || 0} times
                    </span>
                  </div>
                  {template.description && (
                    <p className="text-xs text-muted-foreground line-clamp-2">
                      {template.description}
                    </p>
                  )}
                </div>
              </CardContent>
              <CardFooter className="p-4 pt-0">
                <Button 
                  className="w-full" 
                  variant="secondary" 
                  onClick={() => handleUseTemplate(template)}
                  data-testid="template-apply-button"
                >
                  Use Template
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      )}

      {/* Preview Dialog */}
      <Dialog open={!!previewTemplate} onOpenChange={(open) => {
        if (!open) {
          setPreviewTemplate(null);
          // If we got here via /templates/:id, drop the id from the URL
          // so the back button / sharing still makes sense.
          if (routeTemplateId) navigate('/templates', { replace: true });
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{previewTemplate?.name}</DialogTitle>
            <DialogDescription>
              {previewTemplate?.description}
              {previewTemplate?.category && (
                <Badge variant="outline" className="capitalize ml-2">
                  {previewTemplate.category.replace('_', ' ')}
                </Badge>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-center p-8">
            <div 
              className="rounded-lg flex items-center justify-center p-4"
              style={{
                backgroundColor: previewTemplate?.design?.background_options?.color || '#ffffff',
                border: '1px solid hsl(var(--border))'
              }}
            >
              {/* QR Code Preview - rendered by QRCodeStyling */}
              <div ref={qrPreviewRef} className="flex items-center justify-center" />
            </div>
          </div>
          {previewTemplate?.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {previewTemplate.tags.map((tag, idx) => (
                <Badge key={idx} variant="secondary" className="text-xs">
                  {tag}
                </Badge>
              ))}
            </div>
          )}
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setPreviewTemplate(null)}>
              Close
            </Button>
            <Button onClick={() => {
              handleUseTemplate(previewTemplate);
              setPreviewTemplate(null);
            }}>
              Use Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTemplate} onOpenChange={() => setDeleteTemplate(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteTemplate?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-button"
            >
              {deleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
